import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import "./AdminAddBasketball.css";

/**
 * Hutumia Firebase Auth ikiwa ipo. Ikiwa Login.jsx ya app hii inahifadhi
 * hali ya login kwenye localStorage, tumia taarifa hizo pia.
 */
function getAppLoginUser() {
  if (auth.currentUser) {
    return {
      uid: auth.currentUser.uid,
      username: auth.currentUser.displayName || "",
      source: "firebase",
    };
  }

  try {
    if (localStorage.getItem("authenticated") !== "true") return null;

    let savedUser = {};
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      try {
        savedUser = JSON.parse(rawUser) || {};
      } catch {
        savedUser = {};
      }
    }

    const username = savedUser.username || localStorage.getItem("username") || "";
    return {
      ...savedUser,
      uid: savedUser.uid || savedUser.id || username,
      username,
      source: "localStorage",
    };
  } catch (error) {
    console.warn("Could not read app login state:", error);
    return null;
  }
}

const COLLECTION = "basketballmatches";
const TIMEZONE = "Africa/Dar_es_Salaam";
const PERIODS = ["Q1", "Q2", "Q3", "Q4", "OT"];

// Correct Score 16 pekee na odds zake maalum.
const BASKETBALL_SCORE_ODDS = [
  { score: "80 - 75", odds: 8.50 },
  { score: "85 - 80", odds: 7.20 },
  { score: "90 - 85", odds: 6.50 },
  { score: "95 - 90", odds: 5.80 },
  { score: "100 - 95", odds: 5.20 },
  { score: "100 - 90", odds: 4.80 },
  { score: "105 - 95", odds: 4.50 },
  { score: "105 - 100", odds: 5.50 },
  { score: "110 - 100", odds: 4.20 },
  { score: "110 - 105", odds: 5.00 },
  { score: "115 - 105", odds: 4.60 },
  { score: "115 - 110", odds: 5.80 },
  { score: "120 - 110", odds: 5.20 },
  { score: "120 - 115", odds: 6.00 },
  { score: "125 - 115", odds: 6.50 },
  { score: "125 - 120", odds: 7.00 },
];

const DEFAULT_SCORE_ODDS = BASKETBALL_SCORE_ODDS;

const EMPTY_FORM = {
  country: "",
  league: "",
  homeTeam: "",
  awayTeam: "",
  date: "",
  time: "",
  homeOdds: "",
  awayOdds: "",
};

const EMPTY_SCORE = {
  homeQ1: "",
  awayQ1: "",
  homeQ2: "",
  awayQ2: "",
  homeQ3: "",
  awayQ3: "",
  homeQ4: "",
  awayQ4: "",
  homeOT: "",
  awayOT: "",
  homeTotal: "",
  awayTotal: "",
};

function safeNumber(value) {
  if (value === "" || value == null) return null;
  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    return false;
  }

  const date = new Date(`${value}T12:00:00Z`);

  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
}

function getEATDate(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = Number(parts.find((part) => part.type === "year").value);
  const month = Number(parts.find((part) => part.type === "month").value);
  const day = Number(parts.find((part) => part.type === "day").value);

  return new Date(
    Date.UTC(year, month - 1, day + offsetDays, 12)
  )
    .toISOString()
    .slice(0, 10);
}

function normaliseTime(value) {
  if (!value) return "";

  const clean = String(value)
    .replace(/#\s*\d+\s*$/, "")
    .trim()
    .replace(/\s+/g, " ");

  let match = clean.match(/^(\d{1,2}):(\d{2})$/);

  if (match) {
    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (hour <= 23 && minute <= 59) {
      return `${String(hour).padStart(2, "0")}:${String(
        minute
      ).padStart(2, "0")}`;
    }
  }

  match = clean.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (match) {
    let hour = Number(match[1]);
    const minute = Number(match[2]);
    const period = match[3].toUpperCase();

    if (hour < 1 || hour > 12 || minute > 59) return "";
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;

    return `${String(hour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}`;
  }

  return "";
}

function parseDateTime(line) {
  if (!line) return null;

  const clean = String(line)
    .replace(/\u00a0/g, " ")
    .replace(/#\s*\d+\s*$/, "")
    .replace(/^\|+|\|+$/g, "")
    .trim()
    .replace(/\s+/g, " ");

  const match = clean.match(
    /^(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+(.+)$/i
  );

  if (!match) return null;

  const datePart = match[1];
  const time = normaliseTime(match[2]);

  if (!time) return null;

  let date;

  if (/^today$/i.test(datePart)) {
    date = getEATDate(0);
  } else if (/^tomorrow$/i.test(datePart)) {
    date = getEATDate(1);
  } else if (/^\d{4}-/.test(datePart)) {
    const [year, month, day] = datePart.split("-").map(Number);

    date = `${String(year).padStart(4, "0")}-${String(
      month
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  } else {
    const [day, month, rawYear] = datePart.split("/").map(Number);
    const year = rawYear < 100 ? rawYear + 2000 : rawYear;

    date = `${String(year).padStart(4, "0")}-${String(
      month
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  if (!isValidDate(date)) return null;

  return { date, time };
}

function cleanCell(value) {
  return String(value || "")
    .replace(/\\/g, "")
    .replace(/<\/?strong>/gi, "")
    .replace(/<\/?b>/gi, "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseLeague(line) {
  const clean = cleanCell(line);
  const parts = clean.split(/\s+-\s+/);

  if (parts.length >= 2) {
    return {
      country: parts[0].trim(),
      league: parts.slice(1).join(" - ").trim(),
    };
  }

  const commaParts = clean.split(/\s*,\s*/);
  if (
    commaParts.length >= 2 &&
    /\b(league|series|liga|cup|championship|open|tour|pro)\b/i.test(clean)
  ) {
    return {
      country: commaParts[0].trim(),
      league: commaParts.slice(1).join(", ").trim(),
    };
  }

  return { country: "Other", league: clean };
}

function isLeagueLine(line) {
  const clean = cleanCell(line);
  const hyphenHeader = /^[^|]+\s+-\s+[^|]+$/.test(clean);
  const commaHeader =
    /^[^|,]+\s*,\s*[^|,]+(?:\s*,\s*[^|,]+)*$/.test(clean) &&
    /\b(league|series|liga|cup|championship|open|tour|pro)\b/i.test(clean);

  return (
    (hyphenHeader || commaHeader) &&
    !/^[-+]?\s*\d/.test(clean) &&
    !/^(today|tomorrow)\b/i.test(clean) &&
    !/^\d{1,2}:\d{2}/.test(clean)
  );
}

function extractPrices(value) {
  return String(value || "").match(/\d+\.\d{2}/g) || [];
}

function parseCompactOdds(value) {
  const prices = extractPrices(
    String(value || "").replace(/\s/g, "")
  );

  if (prices.length < 2) return null;

  const home = Number(prices[0]);
  const away = Number(prices[1]);

  if (
    !Number.isFinite(home) ||
    !Number.isFinite(away) ||
    home < 1.01 ||
    away < 1.01
  ) {
    return null;
  }

  return { home, away };
}

function isOddsCell(value) {
  return (
    extractPrices(value).length >= 2 &&
    !/[a-z]/i.test(String(value || ""))
  );
}

function isHandicapLine(value) {
  return /^[+-]\s*\d+(?:\.\d+)?$/.test(
    String(value || "").trim()
  );
}
function parseBasketballText(rawText) {
  const lines = String(rawText || "")
    .replace(/\r/g, "")
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const matches = [];
  let currentLeague = "";
  let currentMatch = null;
  let pendingTeams = [];
  let pendingOdds = [];
  let pendingHandicap = null;
  let pendingHandicapOdds = null;

  function resetPending() {
    pendingTeams = [];
    pendingOdds = [];
    pendingHandicap = null;
    pendingHandicapOdds = null;
  }

  function finishMatch(dateTime = null) {
    if (currentMatch && dateTime) {
      currentMatch.date = dateTime.date;
      currentMatch.time = dateTime.time;
    }

    if (
      currentMatch &&
      currentMatch.homeTeam &&
      currentMatch.awayTeam &&
      isValidDate(currentMatch.date) &&
      normaliseTime(currentMatch.time)
    ) {
      matches.push({
        ...currentMatch,
        time: normaliseTime(currentMatch.time),
      });
    }

    currentMatch = null;
    resetPending();
  }

  function startMatch(homeTeam, awayTeam) {
    const leagueInfo = parseLeague(currentLeague);

    currentMatch = {
      country: leagueInfo.country,
      league: leagueInfo.league,
      homeTeam: cleanCell(homeTeam),
      awayTeam: cleanCell(awayTeam),
      date: "",
      time: "",
      homeOdds: null,
      awayOdds: null,
      handicap: pendingHandicap,
      handicapHomeOdds: pendingHandicapOdds?.[0] ?? null,
      handicapAwayOdds: pendingHandicapOdds?.[1] ?? null,
    };

    for (const cell of pendingOdds) {
      const odds = parseCompactOdds(cell);

      if (odds) {
        currentMatch.homeOdds = odds.home;
        currentMatch.awayOdds = odds.away;
        break;
      }
    }

    resetPending();
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line || /^[|:\-\s]+$/.test(line)) continue;

    const cells = line.includes("|")
      ? line
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map(cleanCell)
          .filter(Boolean)
      : line.split(/\t+/).map(cleanCell).filter(Boolean);

    const cleanedLine = cleanCell(line);
    const firstCell = cleanCell(cells[0] || cleanedLine);

    if (
      isLeagueLine(firstCell) &&
      !cells.some((cell) => isOddsCell(cell))
    ) {
      finishMatch();
      currentLeague = firstCell;
      resetPending();
      continue;
    }

    if (!currentLeague) continue;

    const dateTime = parseDateTime(cleanedLine);

    if (dateTime) {
      if (currentMatch) {
        finishMatch(dateTime);
      } else if (pendingTeams.length >= 2) {
        startMatch(pendingTeams[0], pendingTeams[1]);
        finishMatch(dateTime);
      }

      continue;
    }

    if (/^\+\s*\d+$/.test(cleanedLine)) continue;

    if (isHandicapLine(cleanedLine)) {
      pendingHandicap = Number(cleanedLine.replace(/\s/g, ""));

      if (currentMatch) {
        currentMatch.handicap = pendingHandicap;
      }

      continue;
    }

    const handicapPrices = cleanedLine.match(
      /^\s*(\d+\.\d{2})\s+(\d+\.\d{2})\s*$/
    );

    if (handicapPrices && pendingHandicap != null) {
      pendingHandicapOdds = [
        Number(handicapPrices[1]),
        Number(handicapPrices[2]),
      ];

      if (currentMatch) {
        currentMatch.handicapHomeOdds = pendingHandicapOdds[0];
        currentMatch.handicapAwayOdds = pendingHandicapOdds[1];
      }

      continue;
    }

    if (line.includes("|")) {
      const rowCells = line
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map(cleanCell)
        .filter(Boolean);

      const teamCells = rowCells.filter((cell) => {
        if (!cell || isOddsCell(cell) || parseDateTime(cell)) {
          return false;
        }

        if (/^[+-]?\s*\d+(?:\.\d+)?$/.test(cell)) return false;
        if (/^#?\d{4,}$/.test(cell)) return false;
        if (/^:?-{2,}:?$/.test(cell)) return false;

        return true;
      });

      if (teamCells.length >= 2) {
        finishMatch();
        startMatch(teamCells[0], teamCells[1]);

        for (const cell of rowCells) {
          const odds = parseCompactOdds(cell);

          if (odds && currentMatch.homeOdds == null) {
            currentMatch.homeOdds = odds.home;
            currentMatch.awayOdds = odds.away;
            break;
          }
        }
      }

      continue;
    }

    if (isOddsCell(cleanedLine)) {
      if (currentMatch) {
        if (currentMatch.homeOdds == null) {
          const odds = parseCompactOdds(cleanedLine);

          if (odds) {
            currentMatch.homeOdds = odds.home;
            currentMatch.awayOdds = odds.away;
          }
        }
      } else {
        pendingOdds.push(cleanedLine);
      }

      continue;
    }

    if (
      /^#?\d{4,}$/.test(cleanedLine) ||
      /^[+-]?\s*\d+(?:\.\d+)?$/.test(cleanedLine) ||
      /^(home|away|odds)$/i.test(cleanedLine)
    ) {
      continue;
    }

    if (currentMatch) continue;

    pendingTeams.push(cleanCell(cleanedLine));

    if (pendingTeams.length === 2) {
      startMatch(pendingTeams[0], pendingTeams[1]);
    }
  }

  finishMatch();

  return matches.filter(
    (match) =>
      isValidDate(match.date) &&
      Boolean(normaliseTime(match.time)) &&
      Boolean(match.homeTeam) &&
      Boolean(match.awayTeam) &&
      match.homeTeam.toLowerCase() !== match.awayTeam.toLowerCase()
  );
}

function getQuarterTotal(score, side) {
  const values = PERIODS.map((period) =>
    safeNumber(score[`${side}${period}`])
  );

  if (values.every((value) => value == null)) return null;

  return values.reduce((sum, value) => sum + (value ?? 0), 0);
}

// Hapa tunatumia scores 16 pekee na odds zake zilizowekwa.
// Hatuhifadhi odds za zamani wala kuongeza scores nyingine.
function makeScoreOdds() {
  return BASKETBALL_SCORE_ODDS.map((item) => ({
    score: item.score,
    odds: String(item.odds),
  }));
}

export default function AdminAddBasketball() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [score, setScore] = useState(EMPTY_SCORE);
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [scoreOdds, setScoreOdds] = useState(() => makeScoreOdds());
  const [scoreSearch, setScoreSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const preview = useMemo(
    () => parseBasketballText(bulkText),
    [bulkText]
  );

  const filteredScoreOdds = useMemo(() => {
    const query = scoreSearch.trim().toLowerCase();

    return query
      ? scoreOdds.filter((item) =>
          item.score.toLowerCase().includes(query)
        )
      : scoreOdds;
  }, [scoreOdds, scoreSearch]);

  async function loadMatches() {
    setLoading(true);
    setError("");

    try {
      const snapshot = await getDocs(collection(db, COLLECTION));

      const rows = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      rows.sort((a, b) =>
        `${a.date || ""} ${a.time || ""}`.localeCompare(
          `${b.date || ""} ${b.time || ""}`
        )
      );

      setMatches(rows);
    } catch (err) {
      setError(
        err?.message || "Imeshindikana kusoma basketballmatches."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMatches();
  }, []);

  function handleFormChange(event) {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }

  function handleScoreChange(event) {
    setScore((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }

  function updateScoreOdd(scoreName, value) {
    setScoreOdds((current) =>
      current.map((item) =>
        item.score === scoreName
          ? { ...item, odds: value }
          : item
      )
    );
  }

  function resetScoreOdds() {
    setScoreOdds(makeScoreOdds());
    setScoreSearch("");
  }

  function validateScoreOdds() {
    const invalid = scoreOdds.find((item) => {
      const odds = safeNumber(item.odds);
      return odds == null || odds < 1.01;
    });

    return invalid
      ? `Weka odds halali (angalau 1.01) kwa score ${invalid.score}.`
      : "";
  }

  function serialiseScoreOdds() {
    return BASKETBALL_SCORE_ODDS.map((item) => ({
      score: item.score,
      odds: item.odds,
    }));
  }
  async function addMatch(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    const required = [
      form.country,
      form.league,
      form.homeTeam,
      form.awayTeam,
      form.date,
      form.time,
    ];

    if (required.some((value) => !String(value).trim())) {
      setError("Jaza nchi, ligi, timu, tarehe na muda.");
      return;
    }

    if (!isValidDate(form.date) || !normaliseTime(form.time)) {
      setError("Tarehe au muda si sahihi.");
      return;
    }

    if (
      form.homeTeam.trim().toLowerCase() ===
      form.awayTeam.trim().toLowerCase()
    ) {
      setError("Timu mbili haziwezi kuwa sawa.");
      return;
    }

    const homeOdds = safeNumber(form.homeOdds);
    const awayOdds = safeNumber(form.awayOdds);

    if (
      (homeOdds != null && homeOdds < 1.01) ||
      (awayOdds != null && awayOdds < 1.01)
    ) {
      setError("Odds lazima ziwe 1.01 au zaidi.");
      return;
    }

    const oddsError = validateScoreOdds();

    if (oddsError) {
      setError(oddsError);
      return;
    }

    setSaving(true);

    try {
      const homeTeam = form.homeTeam.trim();
      const awayTeam = form.awayTeam.trim();

      await addDoc(collection(db, COLLECTION), {
        sport: "basketball",
        country: form.country.trim(),
        league: form.league.trim(),
        homeTeam,
        awayTeam,
        A: homeTeam,
        B: awayTeam,
        date: form.date,
        time: normaliseTime(form.time),
        timezone: TIMEZONE,
        odds: {
          home: homeOdds,
          away: awayOdds,
        },
        scoreOdds: serialiseScoreOdds(),
        score: {
          homeQ1: null,
          awayQ1: null,
          homeQ2: null,
          awayQ2: null,
          homeQ3: null,
          awayQ3: null,
          homeQ4: null,
          awayQ4: null,
          homeOT: null,
          awayOT: null,
          homeTotal: null,
          awayTotal: null,
          homeQuarterTotal: null,
          awayQuarterTotal: null,
        },
        status: "upcoming",
        source: "admin",
        createdBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setForm(EMPTY_FORM);
      resetScoreOdds();
      setSuccess("Mechi na Correct Score zimehifadhiwa.");
      await loadMatches();
    } catch (err) {
      setError(err?.message || "Imeshindikana kuhifadhi mechi.");
    } finally {
      setSaving(false);
    }
  }

  async function importBulkMatches(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    if (!preview.length) {
      setError("Hakuna mechi iliyotambuliwa kwenye maandishi.");
      return;
    }

    setBulkSaving(true);
    let saved = 0;

    try {
      for (const match of preview) {
        await addDoc(collection(db, COLLECTION), {
          sport: "basketball",
          country: match.country || "Other",
          league: match.league || "Unknown League",
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          A: match.homeTeam,
          B: match.awayTeam,
          date: match.date,
          time: normaliseTime(match.time),
          timezone: TIMEZONE,
          odds: {
            home: match.homeOdds,
            away: match.awayOdds,
          },
          handicap: match.handicap ?? null,
          handicapOdds:
            match.handicapHomeOdds != null &&
            match.handicapAwayOdds != null
              ? {
                  home: match.handicapHomeOdds,
                  away: match.handicapAwayOdds,
                }
              : null,
          scoreOdds: serialiseScoreOdds(),
          score: {
            homeQ1: null,
            awayQ1: null,
            homeQ2: null,
            awayQ2: null,
            homeQ3: null,
            awayQ3: null,
            homeQ4: null,
            awayQ4: null,
            homeOT: null,
            awayOT: null,
            homeTotal: null,
            awayTotal: null,
            homeQuarterTotal: null,
            awayQuarterTotal: null,
          },
          status: "upcoming",
          source: "admin-bulk-import",
          createdBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        saved += 1;
      }

      setBulkText("");
      setSuccess(
        `Mechi ${saved} zimehifadhiwa pamoja na Correct Score 16.`
      );
      await loadMatches();
    } catch (err) {
      setError(
        `${saved} zimehifadhiwa kabla ya hitilafu. ${
          err?.message || ""
        }`
      );
      await loadMatches();
    } finally {
      setBulkSaving(false);
    }
  }

  function selectMatch(match) {
    const current = match.score || {};

    setSelectedMatchId(match.id);

    setScore({
      homeQ1: current.homeQ1 ?? "",
      awayQ1: current.awayQ1 ?? "",
      homeQ2: current.homeQ2 ?? "",
      awayQ2: current.awayQ2 ?? "",
      homeQ3: current.homeQ3 ?? "",
      awayQ3: current.awayQ3 ?? "",
      homeQ4: current.homeQ4 ?? "",
      awayQ4: current.awayQ4 ?? "",
      homeOT: current.homeOT ?? "",
      awayOT: current.awayOT ?? "",
      homeTotal: current.homeTotal ?? "",
      awayTotal: current.awayTotal ?? "",
    });

    // Hupakia scores 16 pekee na odds zilizowekwa.
    resetScoreOdds();

    setError("");
    setSuccess("");

    document.getElementById("ab-score-editor")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  async function saveScore() {
    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    if (!selectedMatchId) {
      setError("Chagua mechi kwanza.");
      return;
    }

    const homeTotal = safeNumber(score.homeTotal);
    const awayTotal = safeNumber(score.awayTotal);

    if (
      homeTotal == null ||
      awayTotal == null ||
      !Number.isInteger(homeTotal) ||
      !Number.isInteger(awayTotal)
    ) {
      setError("Ingiza score ya mwisho ya timu zote mbili.");
      return;
    }

    const result = {};

    for (const period of PERIODS) {
      result[`home${period}`] = safeNumber(score[`home${period}`]);
      result[`away${period}`] = safeNumber(score[`away${period}`]);
    }

    result.homeTotal = homeTotal;
    result.awayTotal = awayTotal;
    result.homeQuarterTotal = getQuarterTotal(score, "home");
    result.awayQuarterTotal = getQuarterTotal(score, "away");

    const regularFields = [
      "homeQ1",
      "awayQ1",
      "homeQ2",
      "awayQ2",
      "homeQ3",
      "awayQ3",
      "homeQ4",
      "awayQ4",
    ];

    const hasQuarter = regularFields.some(
      (key) => result[key] != null
    );

    if (hasQuarter) {
      if (regularFields.some((key) => result[key] == null)) {
        setError(
          "Jaza Q1–Q4 kwa timu zote, au acha quarter zote wazi."
        );
        return;
      }

      const calculatedHome =
        result.homeQuarterTotal + (result.homeOT ?? 0);

      const calculatedAway =
        result.awayQuarterTotal + (result.awayOT ?? 0);

      if (
        calculatedHome !== homeTotal ||
        calculatedAway !== awayTotal
      ) {
        setError(
          `Jumla ya quarter/OT ni ${calculatedHome}–${calculatedAway}, lakini Full Time ni ${homeTotal}–${awayTotal}.`
        );
        return;
      }
    }

    const oddsError = validateScoreOdds();

    if (oddsError) {
      setError(oddsError);
      return;
    }

    setSaving(true);

    try {
      await updateDoc(doc(db, COLLECTION, selectedMatchId), {
        score: result,
        scoreOdds: serialiseScoreOdds(),
        status: "finished",
        updatedAt: serverTimestamp(),
        resultUpdatedBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
        resultSavedAt: serverTimestamp(),
      });

      setSuccess(
        "Matokeo na Correct Score odds 16 zimehifadhiwa."
      );

      setSelectedMatchId("");
      setScore(EMPTY_SCORE);
      resetScoreOdds();
      await loadMatches();
    } catch (err) {
      setError(
        err?.message || "Imeshindikana kuhifadhi matokeo."
      );
    } finally {
      setSaving(false);
    }
  }
  const filteredMatches = matches.filter((match) =>
  [
    match.country,
    match.league,
    match.homeTeam,
    match.awayTeam,
    match.A,
    match.B,
  ]
    .join(" ")
    .toLowerCase()
    .includes(search.trim().toLowerCase())
);

const selectedMatch = matches.find(
  (match) => match.id === selectedMatchId
);

return (
  <main className="admin-basketball-page">
    <header className="ab-header">
      <div>
        <span className="ab-eyebrow">ADMIN / SPORTS</span>
        <h1>Admin Add Basketball</h1>
        <p>Ongeza mechi, Correct Score na matokeo.</p>
      </div>

      <div className="ab-count">
        <strong>{matches.length}</strong>
        <span>Matches</span>
      </div>
    </header>

    {error && <div className="ab-alert ab-error">{error}</div>}
    {success && (
      <div className="ab-alert ab-success">{success}</div>
    )}

    {/* BULK IMPORT */}
    <section className="ab-panel ab-bulk-panel">
      <div className="ab-panel-heading">
        <div>
          <h2>Paste / Import Mechi Nyingi</h2>
          <p>Bandika plain text au jedwali la bookmaker.</p>
        </div>
      </div>

      <form className="ab-form" onSubmit={importBulkMatches}>
        <label className="ab-correct-scores-field">
          Bandika bookmaker hapa
          <textarea
            className="ab-bulk-textarea"
            value={bulkText}
            onChange={(event) => setBulkText(event.target.value)}
            placeholder={`Croatia - Premijer liga
+15
Kk Virtus Zagreb
KK Zadar
4.30 1.17
Tomorrow 19:00#20618`}
            rows={12}
            required
          />
        </label>

        <div className="ab-form-actions">
          <button
            className="ab-primary"
            type="submit"
            disabled={bulkSaving || preview.length === 0}
          >
            {bulkSaving
              ? "Ina-import..."
              : `Import Mechi ${preview.length}`}
          </button>

          <button
            className="ab-secondary"
            type="button"
            onClick={() => setBulkText("")}
            disabled={bulkSaving}
          >
            Futa Orodha
          </button>
        </div>
      </form>

      <p className="ab-note">
        Kila mechi mpya itapata Correct Score 16 pekee,
        pamoja na odds zilizowekwa.
      </p>

      <div className="ab-bulk-preview">
        <h3>Preview ({preview.length})</h3>

        {preview.length === 0 ? (
          <p className="ab-empty">
            Bandika orodha ili kuona mechi.
          </p>
        ) : (
          <div className="ab-match-list">
            {preview.map((match, index) => (
              <article
                className="ab-match-card"
                key={`${match.homeTeam}-${match.awayTeam}-${index}`}
              >
                <div className="ab-match-meta">
                  <span>
                    {match.country} · {match.league}
                  </span>
                </div>

                <div className="ab-teams">
                  <strong>{match.homeTeam}</strong>
                  <span className="ab-vs">VS</span>
                  <strong>{match.awayTeam}</strong>
                </div>

                <div className="ab-match-foot">
                  <span>
                    {match.date} · {match.time}
                  </span>
                  <span>
                    Winner odds: {match.homeOdds ?? "—"} /{" "}
                    {match.awayOdds ?? "—"}
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>

    {/* ADD SINGLE MATCH */}
    <section className="ab-panel">
      <div className="ab-panel-heading">
        <div>
          <h2>Ongeza Mechi Moja</h2>
          <p>Hifadhi mechi kwa kujaza form.</p>
        </div>
      </div>

      <form className="ab-form" onSubmit={addMatch}>
        <label>
          Nchi
          <input
            name="country"
            value={form.country}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          Ligi
          <input
            name="league"
            value={form.league}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          Timu ya nyumbani
          <input
            name="homeTeam"
            value={form.homeTeam}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          Timu ya ugenini
          <input
            name="awayTeam"
            value={form.awayTeam}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          Tarehe
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          Muda (EAT)
          <input
            type="time"
            name="time"
            value={form.time}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          Odds — nyumbani
          <input
            type="number"
            min="1.01"
            step="0.01"
            name="homeOdds"
            value={form.homeOdds}
            onChange={handleFormChange}
          />
        </label>

        <label>
          Odds — ugenini
          <input
            type="number"
            min="1.01"
            step="0.01"
            name="awayOdds"
            value={form.awayOdds}
            onChange={handleFormChange}
          />
        </label>

        <div className="ab-form-actions">
          <button
            className="ab-primary"
            type="submit"
            disabled={saving}
          >
            {saving ? "Inahifadhi..." : "Hifadhi Mechi"}
          </button>

          <button
            className="ab-secondary"
            type="button"
            onClick={() => setForm(EMPTY_FORM)}
          >
            Futa Form
          </button>
        </div>
      </form>
    </section>

    {/* RESULTS AND CORRECT SCORE */}
    <section className="ab-panel" id="ab-score-editor">
      <div className="ab-panel-heading">
        <div>
          <h2>Matokeo na Correct Score</h2>
          <p>Chagua mechi, weka Full Time Score na odds.</p>
        </div>
      </div>

      {selectedMatch ? (
        <div className="ab-selected-match">
          <span>Mechi iliyochaguliwa</span>
          <strong>
            {selectedMatch.homeTeam || selectedMatch.A} VS{" "}
            {selectedMatch.awayTeam || selectedMatch.B}
          </strong>
        </div>
      ) : (
        <p className="ab-note">
          Chagua mechi kutoka kwenye orodha hapa chini.
        </p>
      )}

      <div className="ab-final-score">
        <label>
          Nyumbani — Full Time
          <input
            type="number"
            min="0"
            step="1"
            name="homeTotal"
            value={score.homeTotal}
            onChange={handleScoreChange}
            disabled={!selectedMatchId || saving}
          />
        </label>

        <span className="ab-score-separator">—</span>

        <label>
          Ugenini — Full Time
          <input
            type="number"
            min="0"
            step="1"
            name="awayTotal"
            value={score.awayTotal}
            onChange={handleScoreChange}
            disabled={!selectedMatchId || saving}
          />
        </label>
      </div>

      <div className="ab-quarter-grid">
        {PERIODS.map((period) => (
          <div className="ab-quarter-card" key={period}>
            <h4>{period === "OT" ? "Overtime" : period}</h4>

            <label>
              Nyumbani
              <input
                type="number"
                min="0"
                step="1"
                name={`home${period}`}
                value={score[`home${period}`]}
                onChange={handleScoreChange}
                disabled={!selectedMatchId || saving}
              />
            </label>

            <label>
              Ugenini
              <input
                type="number"
                min="0"
                step="1"
                name={`away${period}`}
                value={score[`away${period}`]}
                onChange={handleScoreChange}
                disabled={!selectedMatchId || saving}
              />
            </label>
          </div>
        ))}
      </div>

      <div className="ab-correct-score-box">
        <div className="ab-correct-score-heading">
          <div>
            <h3>Correct Score Odds</h3>
            <p>Scores 16 pekee zenye odds maalum.</p>
          </div>

          <strong>{filteredScoreOdds.length} scores</strong>
        </div>

        <input
          className="ab-search"
          value={scoreSearch}
          onChange={(event) => setScoreSearch(event.target.value)}
          placeholder="Tafuta score, mfano 100 - 95..."
          disabled={saving}
        />

        <div className="ab-correct-score-grid">
          {filteredScoreOdds.map((item) => (
            <label
              className="ab-correct-score"
              key={item.score}
            >
              <span>{item.score}</span>

              <input
                type="number"
                min="1.01"
                step="0.01"
                value={item.odds}
                onChange={(event) =>
                  updateScoreOdd(item.score, event.target.value)
                }
                disabled={!selectedMatchId || saving}
                aria-label={`Odds za score ${item.score}`}
              />
            </label>
          ))}
        </div>

        <p className="ab-note">
          Odds zilizowekwa ni za mwanzo. Ukizibadilisha hapa,
          thamani zitakazohifadhiwa zitatoka kwenye orodha
          iliyowekwa na programu.
        </p>
      </div>

      <div className="ab-form-actions">
        <button
          className="ab-primary"
          type="button"
          onClick={saveScore}
          disabled={saving || !selectedMatchId}
        >
          {saving ? "Inahifadhi..." : "Hifadhi Score na Odds"}
        </button>

        <button
          className="ab-secondary"
          type="button"
          onClick={() => {
            setSelectedMatchId("");
            setScore(EMPTY_SCORE);
            resetScoreOdds();
          }}
        >
          Futa Score Form
        </button>
      </div>
    </section>

    {/* SAVED MATCHES */}
    <section className="ab-panel">
      <div className="ab-panel-heading ab-list-heading">
        <div>
          <h2>Mechi Zilizohifadhiwa</h2>
          <p>
            Chagua mechi ili kuingiza matokeo na kuhifadhi Correct
            Score odds.
          </p>
        </div>

        <button
          className="ab-secondary"
          type="button"
          onClick={loadMatches}
          disabled={loading}
        >
          {loading ? "Inapakia..." : "Refresh"}
        </button>
      </div>

      <input
        className="ab-search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Tafuta ligi au timu..."
      />

      {loading ? (
        <p className="ab-empty">Inapakia mechi...</p>
      ) : filteredMatches.length === 0 ? (
        <p className="ab-empty">
          Hakuna mechi iliyopatikana.
        </p>
      ) : (
        <div className="ab-match-list">
          {filteredMatches.map((match) => (
            <article className="ab-match-card" key={match.id}>
              <div className="ab-match-meta">
                <span>
                  {match.country || "Other"} ·{" "}
                  {match.league || "Unknown League"}
                </span>
                <span>{match.status || "upcoming"}</span>
              </div>

              <div className="ab-teams">
                <strong>{match.homeTeam || match.A}</strong>
                <span className="ab-vs">VS</span>
                <strong>{match.awayTeam || match.B}</strong>
              </div>

              <div className="ab-match-foot">
                <span>
                  {match.date || "—"} · {match.time || "—"}
                </span>
                <span>
                  Correct Score:{" "}
                  {Array.isArray(match.scoreOdds)
                    ? match.scoreOdds.length
                    : 0}
                </span>
              </div>

              <div className="ab-form-actions">
                <button
                  className="ab-primary"
                  type="button"
                  onClick={() => selectMatch(match)}
                >
                  Chagua / Hariri
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  </main>
);
}